// LotLingo Portal - Storage Module
// Handles localStorage operations for custom close times and estimate cache

import * as config from './config';
import { STORAGE_KEYS } from './storageKeys';
import { Lot, State } from './types';
import { createLogger } from '../../shared/logger.js';

const log = createLogger('portal:storage');

/**
 * Load custom close times from localStorage
 */
export function loadCustomCloseTimes(): Record<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CUSTOM_CLOSE_TIMES);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (err) {
    log.warn('Failed to load custom close times:', err);
  }
  return {};
}

/**
 * Save custom close times to localStorage
 */
export function saveCustomCloseTimes(customTimes: Record<string, number>): void {
  try {
    localStorage.setItem(STORAGE_KEYS.CUSTOM_CLOSE_TIMES, JSON.stringify(customTimes));
  } catch (err) {
    log.warn('Failed to save custom close times:', err);
  }
}

/**
 * Get effective close time for a lot (custom or scraped)
 */
export function getEffectiveCloseTime(lot: Lot): number | null {
  if (!lot || !lot.id) return null;
  const customTimes = loadCustomCloseTimes();
  if (customTimes[lot.id]) {
    return customTimes[lot.id];
  }
  return lot.closeAt || null;
}

/**
 * Set custom close time for a lot
 */
export function setCustomCloseTime(lotId: string, timestamp: number | null): void {
  const customTimes = loadCustomCloseTimes();
  if (timestamp && Number.isFinite(timestamp)) {
    customTimes[lotId] = timestamp;
  } else {
    delete customTimes[lotId];
  }
  saveCustomCloseTimes(customTimes);
}

/**
 * Check if lot has custom close time
 */
export function hasCustomCloseTime(lotId: string): boolean {
  const customTimes = loadCustomCloseTimes();
  return !!customTimes[lotId];
}

/**
 * Get cached estimate for a lot
 */
import type { PriceReviewMetadata } from '../../types/api';

export function getCachedEstimate(lotId: string): PriceReviewMetadata | null {
  try {
    const cache = JSON.parse(localStorage.getItem(config.ESTIMATE_CACHE_KEY) || '{}');
    return cache[lotId];
  } catch {
    return null;
  }
}

/**
 * Set cached estimate for a lot
 */
export function setCachedEstimate(lotId: string, estimate: PriceReviewMetadata): void {
  try {
    const cache = JSON.parse(localStorage.getItem(config.ESTIMATE_CACHE_KEY) || '{}');
    cache[lotId] = estimate;
    localStorage.setItem(config.ESTIMATE_CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

/**
 * Load pending price review map from localStorage
 */
export function loadPendingPriceReviewMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(config.PENDING_PRICE_REVIEW_STORAGE);
    const obj = raw ? JSON.parse(raw) : null;
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

/**
 * Save pending price review map to localStorage
 */
export function savePendingPriceReviewMap(map: Record<string, number>): void {
  try {
    localStorage.setItem(config.PENDING_PRICE_REVIEW_STORAGE, JSON.stringify(map || {}));
  } catch {}
}

/**
 * Persist pending price review for a lot
 */
export function persistPendingPriceReview(lotId: string, startedAt?: number): void {
  if (!lotId) {
    return;
  }
  const map = loadPendingPriceReviewMap();
  map[lotId] = Number.isFinite(startedAt) ? startedAt! : Date.now();
  savePendingPriceReviewMap(map);
}

/**
 * Remove pending price review for a lot
 */
export function removePendingPriceReview(lotId: string): void {
  if (!lotId) {
    return;
  }
  const map = loadPendingPriceReviewMap();
  if (Object.prototype.hasOwnProperty.call(map, lotId)) {
    delete map[lotId];
    savePendingPriceReviewMap(map);
  }
}
